import i18n from 'answers-chrome-extension/src/i18n';
import createDOMPurify from 'dompurify';

import { IAnswerResponse } from '../../../api/types';
import { ITrackEventOrigin } from '../../../services/mixpanel/types';
import { getToastService, TOAST_BOTTOM_OFFSET } from '../../../services/toast/toast-service';
import { TEXT_HTML, TEXT_PLAIN } from '../../constants';

const DOMPurifier = createDOMPurify();
const ALLOWED_TAGS = ['br', 'li'];
const FORBID_TAGS = ['strong', 'b'];

const uponSanitizeElement = (node: Element) => {
    if (ALLOWED_TAGS.includes(node.tagName?.toLowerCase()) && node.parentNode) {
        node.outerHTML = '\n' + (node.textContent ?? '');
    }
};

export const prepareClipboardItems = (
    answer: IAnswerResponse
): { blobs: Record<string, Blob>; strings: Record<string, string> } => {
    const url = answer?.valid_url ?? answer.url;

    const rawHtml = answer?.reader_is_article_or_section ? '' : answer?.html_long ?? '';

    // Generate the rich text to copy
    const link = `<a href='${url}'>${answer?.title ?? ''}</a>`;
    const htmlSanitized = DOMPurifier.sanitize(rawHtml, { FORBID_TAGS });
    const richText = htmlSanitized ? `${htmlSanitized}<br><br>${link}` : link;
    const html = new Blob([richText], { type: TEXT_HTML });

    // Generate the plain text to copy
    DOMPurifier.addHook('uponSanitizeElement', uponSanitizeElement);
    const description = DOMPurifier.sanitize(rawHtml, {
        ALLOWED_TAGS,
        RETURN_DOM: true,
    }).innerText.replaceAll(/\n *!/gi, '\n');

    const readMoreText = i18n.t('preview.copy.read.more', {
        url: url ?? '',
        ns: 'translations',
    });
    const plainText = description ? `${description}\n\n${readMoreText}` : readMoreText;
    DOMPurifier.removeHook('uponSanitizeElement');

    const text = new Blob([plainText], { type: TEXT_PLAIN });

    return {
        blobs: { [TEXT_HTML]: html, [TEXT_PLAIN]: text },
        strings: { [TEXT_HTML]: richText, [TEXT_PLAIN]: plainText },
    };
};

export const showSuccessToast = (origin?: string, publicSource?: boolean) => {
    const textKey = publicSource ? 'answer.copy.public.success' : 'answer.copy.private.success';

    getToastService().info(i18n.t(textKey), {
        ...(origin === ITrackEventOrigin.PREVIEW_WINDOW && { marginBottom: TOAST_BOTTOM_OFFSET }),
    });
};
